/**
 * Score geometry.
 *
 * Pure arithmetic: given an exercise and a layout choice, work out where every
 * staff line, notehead and interval number goes. The renderer does no geometry
 * of its own, which keeps hit-testing and drawing guaranteed to agree — the same
 * functions answer "where does this note go?" and "what did the finger hit?".
 *
 * The vertical origin is arbitrary; the SVG `viewBox` is derived from the
 * content bounds at the end, so nothing has to be positioned in absolute terms.
 */

import type { Exercise, MusicKey } from '../../core';
import { edoOf, measureCount, signaturePositions, staffPositionOf } from '../../core';
import type { StaffLayout } from '../../state/settings';

/** Half the distance between two staff lines — one diatonic step. */
export const STEP = 7;
/** Distance between staff lines. */
export const SPACE = STEP * 2;
/** Horizontal width of one measure. */
export const MEASURE_WIDTH = 94;
/** Vertical gap between the two staves of a grand staff. */
const GRAND_GAP = 176;
/** Padding beyond the outermost drawn object. */
const PAD_TOP = 20;
const PAD_BOTTOM = 12;
/** Space reserved to the right of the final barline. */
const RIGHT_PAD = 24;
/** Where the first measure starts, before the clef and key signature are sized. */
const CLEF_WIDTH = 56;
const SIG_STEP = 13;
const SIG_PAD = 16;

/** Which clef a staff is drawn in. */
export type Clef = 'treble' | 'bass';

/** One five-line staff. */
export interface Staff {
  /** y of the topmost line. */
  readonly top: number;
  /** Diatonic index sitting on the bottom line: E4 for treble, G2 for bass. */
  readonly bottomD: number;
  readonly clef: Clef;
}

/** Everything the renderer and the hit-tester need. */
export interface ScoreLayout {
  readonly width: number;
  readonly height: number;
  /** viewBox min-y — content is laid out around an arbitrary origin and framed here. */
  readonly viewTop: number;
  /** x of the left edge of measure 0. */
  readonly left: number;
  readonly measureWidth: number;
  readonly measures: number;
  readonly staves: readonly Staff[];
  readonly cfStaff: Staff;
  readonly cpStaff: Staff;
  /** y of the interval rail. */
  readonly railY: number;
  /** Staff position the whole-rest glyph hangs from. */
  readonly restD: number;
  /** Vertical extent of a column highlight. */
  readonly columnTop: number;
  readonly columnHeight: number;
  /** True when both voices share one staff. */
  readonly single: boolean;
}

const TREBLE_BOTTOM = 4 * 7 + 2; // E4
const BASS_BOTTOM = 2 * 7 + 4; // G2

/** y of a diatonic index on a staff. */
export const yOf = (d: number, staff: Staff): number =>
  staff.top + 4 * SPACE - (d - staff.bottomD) * STEP;

/** Diatonic index nearest a y coordinate on a staff. */
export const dAt = (y: number, staff: Staff): number =>
  Math.round((staff.top + 4 * SPACE - y) / STEP) + staff.bottomD;

/** Centre x of a measure. */
export const xOf = (measure: number, layout: ScoreLayout): number =>
  layout.left + measure * layout.measureWidth + layout.measureWidth / 2;

/** x of the barline before a measure. */
export const barlineX = (measure: number, layout: ScoreLayout): number =>
  layout.left + measure * layout.measureWidth;

/** Measure index at an x coordinate, or null when outside the score. */
export function measureAt(x: number, layout: ScoreLayout): number | null {
  const m = Math.floor((x - layout.left) / layout.measureWidth);
  return m < 0 || m >= layout.measures ? null : m;
}

/** How much horizontal room the clef and key signature need. */
export function headerWidth(key: MusicKey): number {
  return CLEF_WIDTH + Math.abs(key.sig) * SIG_STEP + SIG_PAD;
}

/**
 * Lay out a score.
 *
 * Two kinds of extra pitch are taken into account, and they are kept apart on
 * purpose. `contentPitches` (the keyboard caret) push the interval rail down,
 * because the rail must clear anything that reads as part of the music.
 * `reservedPitches` (every note the rules would still allow anywhere) only
 * stretch the frame, so the staff never jumps as ghosts come and go — but they
 * do not open a gap between the staff and its interval numbers.
 */
export function computeLayout(
  ex: Exercise,
  layoutMode: StaffLayout,
  contentPitches: readonly number[] = [],
  reservedPitches: readonly number[] = [],
): ScoreLayout {
  const n = measureCount(ex);
  const single = layoutMode === 'single';
  const left = headerWidth(ex.key);
  const width = left + n * MEASURE_WIDTH + RIGHT_PAD;

  const staves: Staff[] = single
    ? [{ top: 0, bottomD: TREBLE_BOTTOM, clef: 'treble' }]
    : [
        { top: 0, bottomD: TREBLE_BOTTOM, clef: 'treble' },
        { top: GRAND_GAP, bottomD: BASS_BOTTOM, clef: 'bass' },
      ];

  const cfOnTop = ex.cfPos === 'upper';
  const cfStaff = single ? staves[0] : cfOnTop ? staves[0] : staves[1];
  const cpStaff = single ? staves[0] : cfOnTop ? staves[1] : staves[0];

  // Where an empty measure's rest hangs: a staff line, conventionally the fourth
  // one up, but pulled to the student's side of the staff when the two voices
  // share it — so the resting voice reads as the one it belongs to.
  const restD = single && ex.cfPos === 'upper' ? cpStaff.bottomD + 2 : cpStaff.bottomD + 6;

  // Vertical bounds: the staves, the written music, and the rests.
  const content: number[] = [];
  const edo = edoOf(ex);
  for (const staff of staves) content.push(staff.top, staff.top + 4 * SPACE);
  for (let i = 0; i < n; i++) {
    content.push(yOf(staffPositionOf(ex.cf[i], edo), cfStaff));
    content.push(ex.cp[i] != null ? yOf(staffPositionOf(ex.cp[i]!, edo), cpStaff) : yOf(restD, cpStaff));
  }
  for (const pitch of contentPitches) content.push(yOf(staffPositionOf(pitch, edo), cpStaff));

  const contentTop = Math.min(...content);
  const contentBottom = Math.max(...content);

  // The rail sits under the single staff, or between the two staves of a grand.
  const railY = single ? contentBottom + 28 : staves[0].top + 4 * SPACE + 40;

  const reserved = reservedPitches.map((pitch) => yOf(staffPositionOf(pitch, edo), cpStaff));
  const top = Math.min(contentTop, ...reserved) - PAD_TOP;
  const bottom = Math.max(contentBottom, railY, ...reserved) + PAD_BOTTOM;

  const columnTop = top + 4;
  const columnHeight = bottom - PAD_BOTTOM / 2 - columnTop;

  return {
    width,
    height: bottom - top,
    viewTop: top,
    left,
    measureWidth: MEASURE_WIDTH,
    measures: n,
    staves,
    cfStaff,
    cpStaff,
    railY,
    restD,
    columnTop,
    columnHeight,
    single,
  };
}


/** Where each key-signature accidental is drawn on a staff. */
export function signatureMarks(key: MusicKey, staff: Staff): { x: number; y: number }[] {
  return signaturePositions(key.sig, staff.clef).map((d, i) => ({
    x: CLEF_WIDTH + i * SIG_STEP,
    y: yOf(d, staff) + 6,
  }));
}

/** The ledger lines a notehead needs, as y coordinates. */
export function ledgerLines(d: number, staff: Staff): number[] {
  const out: number[] = [];
  const topD = staff.bottomD + 8;
  for (let p = staff.bottomD - 2; p >= d; p -= 2) out.push(yOf(p, staff));
  for (let p = topD + 2; p <= d; p += 2) out.push(yOf(p, staff));
  return out;
}

/**
 * Horizontal nudge for a counterpoint note that would collide with the cantus
 * firmus on a shared staff — the conventional engraving dodge.
 */
export function collisionOffset(ex: Exercise, layout: ScoreLayout, measure: number, d: number): number {
  return collisionOffsetAtStaffPosition(ex, layout, measure, staffPositionOf(d, edoOf(ex)));
}

/** Horizontal nudge for a rest or other object already expressed as a staff position. */
export function collisionOffsetAtStaffPosition(
  ex: Exercise,
  layout: ScoreLayout,
  measure: number,
  d: number,
): number {
  if (!layout.single) return 0;
  const cf = ex.cf[measure];
  return cf != null && Math.abs(d - staffPositionOf(cf, edoOf(ex))) <= 1 ? 15 : 0;
}
